import { useEffect } from 'react';

import {
  EntityInEstateStatus,
  EntityKind,
  TestamentaryEntityKind,
} from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { SURVIVING_SPOUSE_SENTINEL } from '../DispositiveProvisionsForm/hooks/useRecipientOptions';
import {
  DiagramDispositionsFormRecipient,
  SelectedNode,
} from './DispositiveDiagramSync.context';

export interface RecipientDisplayMetadata {
  displayName: string;
  entityKind?: EntityKind;
  testamentaryEntityKind?: TestamentaryEntityKind;
  isCharitableOrganization?: boolean;
  inEstateStatus?: EntityInEstateStatus;
}

/**
 * Enrich a form recipient with the fields the diagram needs, deriving
 * `recipientKind` from the id (avoids races with effects that write the
 * field). Returns null if the recipient has no id or no known metadata.
 */
export function enrichRecipientForDiagram(
  recipient: DispositiveProvisionsFormRecipient,
  metadata: RecipientDisplayMetadata | undefined
): DiagramDispositionsFormRecipient | null {
  if (!recipient.recipientId) return null;
  if (!metadata) return null;
  return {
    ...recipient,
    recipientKind: deriveRecipientKindFromId(recipient.recipientId),
    displayName: metadata.displayName,
    entityKind: metadata.entityKind,
    testamentaryEntityKind: metadata.testamentaryEntityKind,
    isCharitableOrganization: metadata.isCharitableOrganization,
    inEstateStatus: metadata.inEstateStatus,
  };
}

export function deriveRecipientKindFromId(id: string): RecipientKind {
  if (id === SURVIVING_SPOUSE_SENTINEL) return RecipientKind.SurvivingSpouse;
  switch (getPulidKind(id)) {
    case PulidKind.Entity:
      return RecipientKind.Entity;
    case PulidKind.TestamentaryEntity:
      return RecipientKind.TestamentaryEntity;
    case PulidKind.ClientProfile:
      return RecipientKind.Individual;
    case PulidKind.ClientOrganization:
      return RecipientKind.Organization;
    default:
      return RecipientKind.Entity;
  }
}

/**
 * Seed the diagram's `selectedNode` to the form's root entity once on
 * mount, so the diagram has a focus before the user clicks anything.
 */
export function useBootstrapDiagramSelectedNode({
  entityId,
  defaultAfterDeath,
  selectedNode,
  setSelectedNode,
}: {
  entityId: string | null;
  defaultAfterDeath: SelectedNode['afterDeath'];
  selectedNode: SelectedNode | null | undefined;
  setSelectedNode: ((node: SelectedNode) => void) | undefined;
}) {
  useEffect(() => {
    if (!setSelectedNode) return;
    if (selectedNode) return;
    if (!entityId) return;
    setSelectedNode({
      pulid: entityId,
      afterDeath: defaultAfterDeath,
      node: null,
    });
  }, [entityId, defaultAfterDeath, selectedNode, setSelectedNode]);
}
